/**
 * PublishFeedPage — main feed screen for the Publish module.
 *
 * One component powers two audiences via `isPublic`:
 *   isPublic=false  → in-app feed (inside AppShell, auth-only). Tabs for
 *                     Global / Workspace / Mine / Bookmarks. Composer +
 *                     engagement enabled.
 *   isPublic=true   → public landing feed (no AppShell, no auth required).
 *                     Single feed of published posts across every workspace.
 *                     Anonymous users see read-only cards; logged-in users
 *                     get engagement + composer.
 *
 * Search + topic + type filters are shared. The card list uses the shared
 * `<PostCard>` component so both surfaces look and behave identically.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass, Plus, House, BookmarkSimple, Sparkle,
  Confetti, Article as ArticleIcon, Megaphone,
} from "@phosphor-icons/react";
import { PostCard } from "@/components/publish/PostCard";
import { PostComposer } from "@/components/publish/PostComposer";
import { ReportDialog } from "@/components/publish/ReportDialog";
import { MyAnalyticsPanel } from "@/components/publish/MyAnalyticsPanel";
import {
  listPosts, publicFeed, publicTopics, setReaction, removeReaction,
  toggleBookmark, listBookmarks, getSettings, deletePost, sharePost,
  delinkPost, republishPost,
} from "@/lib/publishApi";
import { formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import { devWarn } from "@/lib/devLog";
const TABS = [
  { key: "feed",       label: "Global",    icon: Sparkle,   authRequired: false },
  { key: "workspace",  label: "Workspace", icon: House,     authRequired: true },
  { key: "mine",       label: "Mine",      icon: ArticleIcon, authRequired: true },
  { key: "bookmarks",  label: "Saved",     icon: BookmarkSimple, authRequired: true },
];

const POST_TYPES = [
  { value: "",              label: "All types",       icon: null },
  { value: "story",         label: "Stories",         icon: Confetti },
  { value: "article",       label: "Articles",        icon: ArticleIcon },
  { value: "advertisement", label: "Advertisements",  icon: Megaphone },
];

export default function PublishFeedPage({ isPublic = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const authed = !!user;
  const canPostAd = authed && (user?.roles || []).some(r => ["owner", "admin"].includes(r));

  const [tab, setTab] = useState("feed");
  const [postType, setPostType] = useState("");
  const [topic, setTopic] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [reportPost, setReportPost] = useState(null);

  // Public feed disabled banner
  const [publicDisabled, setPublicDisabled] = useState(false);

  // Load topic list for filter chips
  useEffect(() => {
    publicTopics()
      .then(r => setTopics(r.topics || []))
      .catch(() => setTopics([]));
    if (isPublic) {
      // Warn if super admin disabled landing (though FastAPI already 404s)
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/publish/public/settings`)
        .then(r => r.json())
        .then(s => setPublicDisabled(!s?.public_landing_enabled))
        .catch(() => {});
    }
  }, [isPublic]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { post_type: postType, topic, q: search, limit: 30 };
      let res;
      if (isPublic || !authed) {
        res = await publicFeed(params);
      } else if (tab === "bookmarks") {
        const { items: bm } = await listBookmarks();
        res = { items: (bm || []).map(x => x.post).filter(Boolean) };
      } else {
        res = await listPosts({ ...params, scope: tab });
      }
      setItems(res.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [isPublic, authed, tab, postType, topic, search]);

  useEffect(() => { load(); }, [load]);

  const openPost = (p) => {
    const target = isPublic ? `/publish/${p.id}` : `/app/publish/${p.id}`;
    navigate(target);
  };

  const onReact = async (post, reaction) => {
    if (!authed) { toast.info("Please log in to react"); return; }
    try {
      if (!reaction) {
        await removeReaction(post.id);
        setItems(prev => prev.map(p => p.id === post.id
          ? { ...p, viewer: { ...(p.viewer || {}), reaction: null }, likes_count: Math.max(0, (p.likes_count || 0) - (post.viewer?.reaction === "like" ? 1 : 0)), dislikes_count: Math.max(0, (p.dislikes_count || 0) - (post.viewer?.reaction === "dislike" ? 1 : 0)) }
          : p));
      } else {
        const res = await setReaction(post.id, reaction);
        setItems(prev => prev.map(p => p.id === post.id
          ? { ...p, viewer: { ...(p.viewer || {}), reaction }, likes_count: res.likes_count, dislikes_count: res.dislikes_count }
          : p));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onBookmark = async (post) => {
    if (!authed) { toast.info("Please log in to save posts"); return; }
    try {
      const res = await toggleBookmark(post.id);
      setItems(prev => prev.map(p => p.id === post.id
        ? { ...p, viewer: { ...(p.viewer || {}), bookmarked: res.bookmarked } }
        : p));
      toast.success(res.bookmarked ? "Saved" : "Removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onEdit = (post) => { setEditingPostId(post.id); setComposerOpen(true); };
  const onDelete = async (post) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await deletePost(post.id);
      setItems(prev => prev.filter(p => p.id !== post.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelink = async (post) => {
    const msg = "Delink this post from the public Landing Page?\n\n"
      + "It will be removed from the public feed, search, topic pages, and "
      + "the public detail page. It will remain in your My Posts with all "
      + "its comments, reactions, bookmarks and analytics intact, and you can "
      + "republish it later at the same URL.";
    if (!window.confirm(msg)) return;
    try {
      const updated = await delinkPost(post.id);
      setItems(prev => prev.map(p => p.id === post.id ? { ...p, ...updated } : p));
      toast.success("Post delinked from public feed");
      // Refresh My Posts so status flips immediately.
      if (tab === "mine") load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onRepublish = async (post) => {
    try {
      const updated = await republishPost(post.id);
      setItems(prev => prev.map(p => p.id === post.id ? { ...p, ...updated } : p));
      toast.success(post.status === "delinked" ? "Post republished" : "Post published");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onShare = async (post) => {
    // Copy the public URL to the clipboard so users can paste anywhere.
    const url = `${window.location.origin}/publish/${post.id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      } else {
        // Fallback for older browsers.
        window.prompt("Copy this link:", url);
      }
      if (authed) {
        try {
          const res = await sharePost(post.id);
          setItems(prev => prev.map(p => p.id === post.id
            ? { ...p, shares_count: res.shares_count }
            : p));
        } catch (err) {
          // Best-effort counter — the clipboard copy already succeeded, so
          // don't disrupt UX with a toast, but log for observability.
          devWarn("[publish] share counter bump failed", err);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onSaved = () => {
    setEditingPostId(null);
    load();
  };

  const container = isPublic
    ? "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
    : "space-y-4";

  return (
    <div className={container} data-testid="publish-feed-page">
      {/* Header — public landing gets marketing-style hero; in-app gets a compact bar. */}
      {isPublic ? (
        <PublicHeader authed={authed} onOpenApp={() => navigate("/app/publish")} />
      ) : (
        <InAppHeader
          canPost={authed}
          onCompose={() => { setEditingPostId(null); setComposerOpen(true); }}
        />
      )}

      {publicDisabled && isPublic && (
        <div className="my-6 border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-4 py-3 text-sm">
          The public feed has been temporarily disabled by the platform administrator.
        </div>
      )}

      {/* Tabs (in-app only) */}
      {!isPublic && (
        <div className="flex items-center gap-1 overflow-x-auto border-b border-border pb-1">
          {TABS.filter(t => !t.authRequired || authed).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md border-b-2 transition-colors",
                tab === t.key
                  ? "border-primary text-foreground font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              data-testid={`publish-tab-${t.key}`}
            >
              <t.icon size={14} weight={tab === t.key ? "fill" : "regular"} />
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center pt-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search stories, articles, authors…"
            className="pl-8 h-9 text-sm"
            data-testid="publish-search"
          />
        </div>
        <Select value={postType || "all"} onValueChange={(v) => setPostType(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="publish-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {POST_TYPES.map(t => (
              <SelectItem key={t.value || "all"} value={t.value || "all"}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {topic && (
          <Badge className="gap-1 pl-2 pr-1 bg-muted text-foreground" variant="outline">
            #{topic}
            <button onClick={() => setTopic("")} className="hover:bg-background rounded px-0.5">×</button>
          </Badge>
        )}
      </div>

      {/* Topic chips */}
      {topics.length > 0 && !topic && (
        <div className="flex flex-wrap gap-1.5 pt-1" data-testid="topic-chips">
          {topics.slice(0, 12).map(t => (
            <button key={t} onClick={() => setTopic(t)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-border bg-card hover:bg-muted transition-colors">
              #{t}
            </button>
          ))}
        </div>
      )}

      {/* Analytics panel (only on Mine tab) */}
      {!isPublic && tab === "mine" && authed && items.length > 0 && !loading && (
        <MyAnalyticsPanel posts={items} />
      )}

      {/* Feed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={`feed-skeleton-${i}`} className="border rounded-2xl h-64 bg-muted/40 animate-pulse" />
          ))
        ) : items.length === 0 ? (
          <EmptyState tab={tab} isPublic={isPublic} authed={authed}
                      onCompose={() => { setEditingPostId(null); setComposerOpen(true); }} />
        ) : (
          items.map(p => (
            <PostCard
              key={p.id}
              post={p}
              interactive={authed}
              viewer={p.viewer || {
                is_author: authed && p.author_user_id === user?.id,
                can_moderate: user?.platform_role === "super_admin",
                can_delink: authed && (
                  p.author_user_id === user?.id ||
                  (p.post_type === "advertisement"
                    && p.org_id === user?.org_id
                    && (user?.roles || []).some(r => ["owner", "admin"].includes(r)))
                ),
                can_republish: authed && (
                  p.author_user_id === user?.id ||
                  (p.post_type === "advertisement"
                    && p.org_id === user?.org_id
                    && (user?.roles || []).some(r => ["owner", "admin"].includes(r)))
                ),
              }}
              onOpen={openPost}
              onReact={(r) => onReact(p, r)}
              onBookmark={() => onBookmark(p)}
              onReport={setReportPost}
              onShare={onShare}
              onEdit={authed ? onEdit : null}
              onDelete={authed ? onDelete : null}
              onDelink={authed ? onDelink : null}
              onRepublish={authed ? onRepublish : null}
            />
          ))
        )}
      </div>

      {composerOpen && (
        <PostComposer
          open={composerOpen} onOpenChange={setComposerOpen}
          canPostAd={canPostAd}
          editingPostId={editingPostId}
          topicSuggestions={topics}
          onSaved={onSaved}
        />
      )}
      {reportPost && (
        <ReportDialog
          post={reportPost} open={!!reportPost}
          onOpenChange={(v) => !v && setReportPost(null)}
          onReported={() => load()}
        />
      )}
    </div>
  );
}

// -------------------- headers & empty state --------------------

function InAppHeader({ canPost, onCompose }) {
  return (
    <header className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Publish</h1>
        <p className="text-sm text-muted-foreground">
          Share stories, articles, and announcements with your team and the world.
        </p>
      </div>
      {canPost && (
        <Button onClick={onCompose} data-testid="publish-new-post-btn"
                className="bg-primary hover:bg-primary/90">
          <Plus size={16} className="mr-1.5" /> New post
        </Button>
      )}
    </header>
  );
}

function PublicHeader({ authed, onOpenApp }) {
  return (
    <header className="text-center py-8 sm:py-12 space-y-3">
      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
        Community stories & articles
      </Badge>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
        Real voices from<br />therapy centers.
      </h1>
      <p className="max-w-2xl mx-auto text-sm sm:text-base text-muted-foreground">
        Real stories, expert-written articles, and community announcements from
        therapists, families, and staff on TherapyStack Pro.
      </p>
      <div className="pt-2">
        {authed ? (
          <Button onClick={onOpenApp} variant="outline" data-testid="publish-back-to-app-btn">
            Back to app
          </Button>
        ) : (
          <Link to="/login">
            <Button variant="outline" data-testid="publish-login-btn">
              Log in to publish
            </Button>
          </Link>
        )}
      </div>
    </header>
  );
}

function EmptyState({ tab, isPublic, authed, onCompose }) {
  const msgMap = {
    feed:      "No posts have been published yet. Be the first!",
    workspace: "No posts from your workspace yet.",
    mine:      "You haven't published anything yet.",
    bookmarks: "You haven't bookmarked any posts yet.",
  };
  const msg = isPublic ? "No posts to show right now." : (msgMap[tab] || "No posts.");
  return (
    <div className="col-span-full border border-dashed border-border rounded-2xl p-10 text-center space-y-3"
         data-testid="publish-empty-state">
      <p className="text-sm text-muted-foreground">{msg}</p>
      {authed && !isPublic && (tab === "mine" || tab === "feed") && (
        <Button onClick={onCompose}><Plus size={14} className="mr-1" /> Write your first post</Button>
      )}
    </div>
  );
}
